import React, { useEffect, useState, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import debounce from 'lodash.debounce';
import { useIsSmallScreen, useIsKeplerPlatform } from 'utils/hooks';
import * as uiActions from 'actions/ui';
import { getCurrentScrollPosition } from './navigationUtils';
import TopNavigation from './TopNavigation';
import PersistentNavigation from './PersistentNavigation';
import VerticalMenu from './VerticalMenu';

import { useGetHeaderDataLazyQuery } from './headerQuery.generated';
import {
  HeaderWrapper,
  HeaderContainer,
  HeaderSpacer,
  Content,
  LogoLink,
  LogoImage,
  MenuNavigationWrapper,
  PersistentNavigationContainer,
  MenuWrapper,
} from './Styles';

const DEFAULT_MENU_ITEM_AVAILABILITY = ['logged-in', 'logged-out', 'open-service'];
const VISIBILITY_THRESHOLD = 10; // Buffer (px) to prevent flickering near breakpoints

function isMenuItemVisible(availability, isLoggedIn, isOpenServiceActive) {
  const availabilityRules = availability || DEFAULT_MENU_ITEM_AVAILABILITY;
  return (availabilityRules.includes('logged-in') && isLoggedIn)
    || (availabilityRules.includes('open-service') && isOpenServiceActive)
    || (availabilityRules.includes('logged-out') && !isLoggedIn);
}

function Header({ hideHeader = false }) {
  const [scrollStarted, setScrollStarted] = useState(false);
  const menuRef = useRef(null);
  const contentRef = useRef(null);
  const dispatch = useDispatch();

  const {
    brandName,
    headerLogo,
    headerTransparencySupported,
    headerOpacity,
    paymentMandatory,
    entitledPacks,
    userLoggedIn,
    horizontalMenuHidden,
    isAnonymousUser,
    signUpEnabled,
    navigation,
    isOpenServiceActive,
    isCreateAccountEnabled,
    partner,
  } = useSelector(state => ({
    brandName: state.settings.brand.name,
    headerLogo: state.settings.brand.headerLogo,
    headerTransparencySupported: state.ui.headerTransparency[
      `${state.router.location.name}:${state.router.location.name === 'home'
        ? state.settings.navigation.menu[state.settings.navigation.defaultIndex].identifier
        : state.router.location.params.id}`
    ],
    headerOpacity: state.settings.headerOpacity,
    entitledPacks: state.packs.entitled,
    userLoggedIn: state.auth.isLoggedIn,
    paymentMandatory: state.auth.isLoggedIn
      && state.settings.features.payment?.onboarding?.mandatory,
    horizontalMenuHidden: state.ui.horizontalMenuHidden,
    isAnonymousUser: state.auth.isAnonymousUser,
    signUpEnabled: state.settings.features.register,
    navigation: state.settings.navigation,
    isOpenServiceActive: state.country.isOpenServiceActive,
    isCreateAccountEnabled: state.country.isCreateAccountEnabled,
    partner: state.settings.partner,
  }), shallowEqual);

  const entitlementRequest = useRef(false);
  const [ loadEntitlements, { data } ] = useGetHeaderDataLazyQuery();

  // Use a ref to access the latest value in the closure without re-running the effect
  const hiddenRef = useRef(horizontalMenuHidden);
  hiddenRef.current = horizontalMenuHidden;
  const signUpBtnRef = useRef(null);

  if (!entitlementRequest.current && paymentMandatory && userLoggedIn && !entitledPacks?.length) {
    entitlementRequest.current = true;
    loadEntitlements();
  }

  useEffect(() => {
    let unmounted = false;
    const onScroll = () => {
      if (unmounted) {
        return;
      }

      setScrollStarted(getCurrentScrollPosition() > 10);
    };

    const onScrollDebounced = debounce(onScroll, 10);

    window.addEventListener('scroll', onScrollDebounced);

    return () => {
      unmounted = true;
      window.removeEventListener('scroll', onScrollDebounced);
    };
  }, []);

  const hideItemsForUnentitled = useMemo(
    () => paymentMandatory &&
      !data?.viewer.entitlements.edges.length
      && !entitledPacks?.length,
    [data?.viewer.entitlements, entitledPacks?.length],
  );

  useEffect(() => {
    if (contentRef?.current) {
      const content = contentRef?.current;
      const resize = () => {
        const menuEl = menuRef.current;
        if (!menuEl) return;

        let shouldHide = false;

        if (hiddenRef.current) {
          // In "ghost" mode (absolute position), check if elements would fit horizontally
          const navEl = menuEl.firstElementChild;
          const persistentEl = navEl?.nextElementSibling;

          if (navEl && persistentEl) {
            // Check if full nav width + persistent items width (incl. hamburger if visible) fits
            let persistentWidth = persistentEl.offsetWidth;
            if (signUpBtnRef.current) {
              persistentWidth += signUpBtnRef.current.offsetWidth;
            }

            const needed = navEl.scrollWidth + persistentWidth;
            const available = menuEl.clientWidth;
            shouldHide = needed > available - VISIBILITY_THRESHOLD;
          } else {
            shouldHide = true;
          }
        } else {
          // In normal mode, check if wrapping occurred (height increased)
          shouldHide = menuEl.clientHeight > content.clientHeight;
        }

        dispatch(uiActions.hideHorizontalMenu(shouldHide));
      };

      const debouncedResize = debounce(resize, 100);

      const ro = new ResizeObserver(debouncedResize);
      ro.observe(content);
      ro.observe(menuRef.current);
      resize();

      return () => {
        ro.disconnect();
        debouncedResize.cancel();
      };
    }
  }, [!contentRef?.current, userLoggedIn, isAnonymousUser]);

  // see https://magine.atlassian.net/browse/MDM-10551
  const headerContainerOpacity = useMemo(() => {
    let beforeScroll = {
      gradientStart: headerOpacity.regular,
      gradientEnd: headerOpacity.regular,
    };
    let afterScroll = headerOpacity.regular;
    if (headerTransparencySupported) {
      beforeScroll = {
        gradientStart: headerOpacity.transparentStart,
        gradientEnd: headerOpacity.transparent,
      };
    }

    return { beforeScroll, afterScroll };
  }, [headerTransparencySupported, horizontalMenuHidden]);

  // Sappa has signup available but hides it for end users
  // Kepler users should not see signup option
  const isKeplerPlatform = useIsKeplerPlatform();
  const shouldShowSignUp = signUpEnabled && isCreateAccountEnabled && (!userLoggedIn || isAnonymousUser) && partner !== 'sappa' && !isKeplerPlatform;

  const { visibleMenuItems, persistentNavItems } = useMemo(() => {
    if (!navigation?.menu) return { visibleMenuItems: [], persistentNavItems: [] };

    const allVisibleItems = navigation.menu.filter(item =>
      isMenuItemVisible(item.available, userLoggedIn && !isAnonymousUser, isOpenServiceActive));

    const persistentTypes = ['search', 'settings'];
    const persistentItems = allVisibleItems.filter(item => persistentTypes.includes(item.type));
    const regularItems = allVisibleItems.filter(item => !persistentTypes.includes(item.type));

    return {
      visibleMenuItems: regularItems,
      persistentNavItems: persistentItems,
    };
  }, [navigation?.menu, userLoggedIn, isAnonymousUser, isOpenServiceActive]);

  const defaultMenuItem = navigation?.menu?.[navigation?.defaultIndex];

  return (
    <HeaderWrapper hidden={hideHeader}>
      <HeaderContainer
        opacity={headerContainerOpacity}
        scroll={`${scrollStarted}`}
      >
        <Content innerRef={contentRef}>
          <div>
            <LogoLink to={{ name: 'home' }}>
              <LogoImage
                src={headerLogo}
                alt={brandName}
              />
            </LogoLink>
          </div>
          <MenuWrapper innerRef={menuRef} isHidden={horizontalMenuHidden === undefined}>
            <MenuNavigationWrapper hidden={horizontalMenuHidden}>
              <TopNavigation
                position="horizontal"
                visibleMenuItems={hideItemsForUnentitled ? [] : visibleMenuItems}
                defaultMenuItem={defaultMenuItem}
              />
            </MenuNavigationWrapper>

            <PersistentNavigationContainer data-cy="persistent-navigation" wide={shouldShowSignUp && !horizontalMenuHidden}>
              {/*Search + User menu*/}
              <PersistentNavigation
                shouldShowSignUp={shouldShowSignUp}
                persistentNavItems={persistentNavItems}
                signUpBtnRef={signUpBtnRef}
              />
              {horizontalMenuHidden && (visibleMenuItems.length > 0 || shouldShowSignUp) && (
                <VerticalMenu
                  shouldShowSignUp={shouldShowSignUp}
                  visibleMenuItems={hideItemsForUnentitled ? [] : visibleMenuItems}
                  defaultMenuItem={defaultMenuItem}
                />
              )}
            </PersistentNavigationContainer>
          </MenuWrapper>
        </Content>
      </HeaderContainer>

      {!headerTransparencySupported && (
        <HeaderSpacer />
      )}
    </HeaderWrapper>
  );
}

Header.propTypes = {
  hideHeader: PropTypes.bool,
};

export default React.memo(Header);
